// Panelen där en tjänst ändras, med alla fem prissättningssätten (designen
// Admin-Katalog och Admin-Katalog-Timpris). Ändringarna sparas i utkastet.
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { OgiltigKatalog, felText } from '../../katalog/useKatalog.js';
import {
  BOLAGSFORMER,
  DRIVARE,
  drivare,
  PERIODER,
  PRISSATTNINGAR,
  type Billing,
  type LegalForm,
  type PricingType,
} from '../../katalog/texter.js';
import {
  mojligaAlltidMed,
  tillTjanst,
  type Fel,
  type TjanstForm,
  type Tidsnorm,
  type Visning,
} from '../../katalog/tjanstform.js';
import { useKatalogKontext } from './Katalog.js';

interface Props {
  start: TjanstForm;
  onAndrad: (andrad: boolean) => void;
  onStang: () => void;
  /** Tjänstens id efter sparandet, eller null om den togs bort. */
  onSparad: (id: string | null) => void;
}

const VISNINGAR: { kod: Visning; namn: string; beskrivning: string }[] = [
  {
    kod: 'hoursAndRate',
    namn: 'Timmar och timpris',
    beskrivning: 'Till exempel 5,5–7 tim × 750 kr',
  },
  { kod: 'amountOnly', namn: 'Bara belopp', beskrivning: 'Till exempel 4 125–5 250 kr' },
  { kod: 'monthlyCap', namn: 'Tak per månad', beskrivning: 'Faktisk tid debiteras, högst taket' },
];

const AVRUNDNINGAR = [
  { varde: '0.25', namn: 'Kvart' },
  { varde: '0.5', namn: 'Halvtimme' },
  { varde: '1', namn: 'Hel timme' },
];

/** Ett textfält med etikett, hjälptext och fel. */
function Falt({
  etikett,
  fel,
  hjalp,
  efter,
  children,
}: {
  etikett: string;
  fel?: string | undefined;
  hjalp?: string;
  efter?: string;
  children: (props: {
    id: string;
    'aria-invalid'?: true;
    'aria-describedby'?: string;
  }) => ReactNode;
}) {
  const id = useId();
  const beskrivning = [hjalp ? `${id}-hjalp` : '', fel ? `${id}-fel` : '']
    .filter(Boolean)
    .join(' ');
  return (
    <div className="falt">
      <label className="etikett" htmlFor={id}>
        {etikett}
      </label>
      <div className={efter ? 'med-enhet' : undefined}>
        {children({
          id,
          ...(fel ? { 'aria-invalid': true as const } : {}),
          ...(beskrivning ? { 'aria-describedby': beskrivning } : {}),
        })}
        {efter && <span>{efter}</span>}
      </div>
      {hjalp && (
        <div className="hjalp" id={`${id}-hjalp`}>
          {hjalp}
        </div>
      )}
      {fel && (
        <div className="faltfel" id={`${id}-fel`}>
          {fel}
        </div>
      )}
    </div>
  );
}

export function TjanstPanel({ start, onAndrad, onStang, onSparad }: Props) {
  const { katalog, data, kanAndra } = useKatalogKontext();
  const [f, setF] = useState(start);
  const [fel, setFel] = useState<Fel>({});
  const [katalogfel, setKatalogfel] = useState<string[]>([]);
  const [sparfel, setSparfel] = useState<string | null>(null);
  const rubrik = useRef<HTMLHeadingElement>(null);
  const ny = start.id === null;

  useEffect(() => {
    rubrik.current?.focus();
  }, []);

  function andra(nytt: Partial<TjanstForm>) {
    setF((gammalt) => ({ ...gammalt, ...nytt }));
    onAndrad(true);
  }

  const ovriga = katalog.services.filter((s) => s.id !== f.id);

  async function spara() {
    setSparfel(null);
    setKatalogfel([]);
    const resultat = tillTjanst(
      f,
      ovriga.map((s) => s.id),
    );
    if (resultat.fel) {
      setFel(resultat.fel);
      return;
    }
    setFel({});
    const tjanst = resultat.tjanst;
    const services = ny
      ? [...katalog.services, tjanst]
      : katalog.services.map((s) => (s.id === tjanst.id ? tjanst : s));
    try {
      await data.spara.mutateAsync({ ...katalog, services });
      onSparad(tjanst.id);
    } catch (err) {
      if (err instanceof OgiltigKatalog) setKatalogfel(err.fel.map((e) => e.message));
      setSparfel(felText(err));
    }
  }

  async function taBort() {
    if (!f.id || !window.confirm(`Ta bort ${f.label} ur katalogen?`)) return;
    setSparfel(null);
    try {
      await data.spara.mutateAsync({ ...katalog, services: ovriga });
      onSparad(null);
    } catch (err) {
      setSparfel(felText(err));
    }
  }

  const sparar = data.spara.isPending;

  return (
    <aside className="kort panel" aria-labelledby="panel-rubrik">
      <div className="panel-huvud">
        <h2 id="panel-rubrik" ref={rubrik} tabIndex={-1}>
          {ny ? 'Ny tjänst' : start.label}
        </h2>
        <button type="button" className="btn btn-g stang" aria-label="Stäng" onClick={onStang}>
          ×
        </button>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void spara();
        }}
        noValidate
      >
        <fieldset className="panel-falt" disabled={!kanAndra || sparar}>
          <legend className="visuellt-dold">{ny ? 'Ny tjänst' : start.label}</legend>
          <Falt etikett="Namn i offerten" fel={fel.label}>
            {(p) => (
              <input
                className="in"
                {...p}
                value={f.label}
                onChange={(e) => andra({ label: e.target.value })}
              />
            )}
          </Falt>
          <Falt etikett="Det här ingår" hjalp="En punkt per rad. Visas under raden i offerten.">
            {(p) => (
              <textarea
                className="in"
                rows={2}
                {...p}
                value={f.includes}
                onChange={(e) => andra({ includes: e.target.value })}
              />
            )}
          </Falt>
          <Falt etikett="Period">
            {(p) => (
              <select
                className="in"
                {...p}
                value={f.billing}
                onChange={(e) => andra({ billing: e.target.value as Billing })}
              >
                {PERIODER.map((per) => (
                  <option key={per.kod} value={per.kod}>
                    {per.namn}
                  </option>
                ))}
              </select>
            )}
          </Falt>
          <fieldset className="grupp-falt">
            <legend className="etikett">Gäller</legend>
            <div className="hjalp" id="galler-hjalp">
              Välj inga för att tjänsten ska gälla alla bolagsformer.
            </div>
            <div className="kryssrad">
              {BOLAGSFORMER.map((b) => (
                <label key={b.kod} className="kryss">
                  <input
                    type="checkbox"
                    aria-describedby="galler-hjalp"
                    checked={f.appliesTo.includes(b.kod)}
                    onChange={(e) =>
                      andra({
                        appliesTo: e.target.checked
                          ? [...f.appliesTo, b.kod]
                          : f.appliesTo.filter((k) => k !== b.kod),
                      })
                    }
                  />
                  {b.namn}
                </label>
              ))}
            </div>
          </fieldset>
          <AlltidMed f={f} andra={andra} />
          <fieldset className="grupp-falt">
            <legend className="etikett">Hur räknas priset?</legend>
            <div className="val-lista">
              {PRISSATTNINGAR.map((p) => (
                <label key={p.typ} className={`val${f.typ === p.typ ? ' valt' : ''}`}>
                  <input
                    type="radio"
                    name="prissattning"
                    checked={f.typ === p.typ}
                    onChange={() => andra({ typ: p.typ as PricingType })}
                  />
                  <span>
                    <span className="val-namn">{p.namn}</span>
                    <span className="val-text">{p.beskrivning}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          <Prisfalt f={f} fel={fel} andra={andra} />
        </fieldset>
        {Object.keys(fel).length > 0 && (
          <div className="meddelande" role="alert">
            Rätta fälten som är markerade.
          </div>
        )}
        {sparfel && (
          <div className="meddelande" role="alert">
            {sparfel}
            {katalogfel.length > 0 && (
              <ul>
                {katalogfel.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        {kanAndra && (
          <div className="panel-fot">
            {!ny ? (
              <button
                type="button"
                className="btn btn-g farlig"
                disabled={sparar}
                onClick={() => void taBort()}
              >
                Ta bort tjänst
              </button>
            ) : (
              <span />
            )}
            <button type="submit" className="btn btn-s" disabled={sparar}>
              {sparar ? 'Sparar …' : 'Spara utkast'}
            </button>
          </div>
        )}
      </form>
    </aside>
  );
}

function Prisfalt({
  f,
  fel,
  andra,
}: {
  f: TjanstForm;
  fel: Fel;
  andra: (nytt: Partial<TjanstForm>) => void;
}) {
  const period = { monthly: 'per månad', yearly: 'per år', once: '' }[f.billing];
  switch (f.typ) {
    case 'fixed':
      return (
        <Falt etikett="Pris" fel={fel['fast.pris']} efter={`kr ${period}`.trim()}>
          {(p) => (
            <input
              className="in mono tal"
              inputMode="decimal"
              {...p}
              value={f.fast.pris}
              onChange={(e) => andra({ fast: { pris: e.target.value } })}
            />
          )}
        </Falt>
      );
    case 'perUnit':
      return (
        <div className="tva">
          <Falt etikett="Enhet" fel={fel['perUnit.enhet']} hjalp="Till exempel anställd">
            {(p) => (
              <input
                className="in"
                {...p}
                value={f.perUnit.enhet}
                onChange={(e) => andra({ perUnit: { ...f.perUnit, enhet: e.target.value } })}
              />
            )}
          </Falt>
          <Falt
            etikett={`Pris per ${f.perUnit.enhet || 'enhet'}`}
            fel={fel['perUnit.pris']}
            efter="kr"
          >
            {(p) => (
              <input
                className="in mono tal"
                inputMode="decimal"
                {...p}
                value={f.perUnit.pris}
                onChange={(e) => andra({ perUnit: { ...f.perUnit, pris: e.target.value } })}
              />
            )}
          </Falt>
        </div>
      );
    case 'tiered':
      return <Trappa f={f} fel={fel} andra={andra} period={period} />;
    case 'byLegalForm':
      return (
        <fieldset className="grupp-falt">
          <legend className="etikett">Pris per bolagsform {period}</legend>
          <div className="hjalp">Lämna tomt för bolagsformer som tjänsten inte gäller.</div>
          {fel.bolagsform && <div className="faltfel">{fel.bolagsform}</div>}
          <div className="tva">
            {BOLAGSFORMER.map((b) => (
              <Falt key={b.kod} etikett={b.namn} fel={fel[`bolagsform.${b.kod}`]} efter="kr">
                {(p) => (
                  <input
                    className="in mono tal"
                    inputMode="decimal"
                    {...p}
                    value={f.bolagsform[b.kod]}
                    onChange={(e) =>
                      andra({
                        bolagsform: { ...f.bolagsform, [b.kod as LegalForm]: e.target.value },
                      })
                    }
                  />
                )}
              </Falt>
            ))}
          </div>
        </fieldset>
      );
    case 'hourly':
      return <Timpris f={f} fel={fel} andra={andra} />;
  }
}

function DrivarVal({
  varde,
  onVal,
  etikett,
}: {
  varde: string;
  onVal: (kod: string) => void;
  etikett: string;
}) {
  const kanda = DRIVARE.some((d) => d.kod === varde);
  return (
    <Falt etikett={etikett}>
      {(p) => (
        <select className="in" {...p} value={varde} onChange={(e) => onVal(e.target.value)}>
          {DRIVARE.map((d) => (
            <option key={d.kod} value={d.kod}>
              {d.namn}
            </option>
          ))}
          {!kanda && <option value={varde}>{varde}</option>}
        </select>
      )}
    </Falt>
  );
}

function Trappa({
  f,
  fel,
  andra,
  period,
}: {
  f: TjanstForm;
  fel: Fel;
  andra: (nytt: Partial<TjanstForm>) => void;
  period: string;
}) {
  const t = f.trappa;
  const steg = (i: number, nytt: Partial<{ upTo: string; pris: string }>) =>
    andra({ trappa: { ...t, steg: t.steg.map((s, j) => (j === i ? { ...s, ...nytt } : s)) } });
  const hogsta = t.steg.at(-1)?.upTo;
  return (
    <>
      <DrivarVal
        etikett="Styrs av"
        varde={t.drivare}
        onVal={(kod) => andra({ trappa: { ...t, drivare: kod } })}
      />
      <fieldset className="grupp-falt">
        <legend className="etikett">Steg</legend>
        <div className="trappsteg rubrik" aria-hidden="true">
          <span>Upp till ({drivare(t.drivare).kort})</span>
          <span>Pris {period}</span>
          <span />
        </div>
        {fel['trappa.steg'] && <div className="faltfel">{fel['trappa.steg']}</div>}
        {t.steg.map((s, i) => {
          const felUpTo = fel[`trappa.steg.${i}.upTo`];
          const felPris = fel[`trappa.steg.${i}.pris`];
          return (
            <div key={i}>
              <div className="trappsteg">
                <input
                  className="in mono tal"
                  inputMode="numeric"
                  aria-label={`Upp till, steg ${i + 1}`}
                  aria-invalid={felUpTo ? true : undefined}
                  value={s.upTo}
                  onChange={(e) => steg(i, { upTo: e.target.value })}
                />
                <input
                  className="in mono tal"
                  inputMode="decimal"
                  aria-label={`Pris i kronor, steg ${i + 1}`}
                  aria-invalid={felPris ? true : undefined}
                  value={s.pris}
                  onChange={(e) => steg(i, { pris: e.target.value })}
                />
                <button
                  type="button"
                  className="btn btn-g ta-bort"
                  aria-label={`Ta bort steg ${i + 1}`}
                  onClick={() =>
                    andra({ trappa: { ...t, steg: t.steg.filter((_, j) => j !== i) } })
                  }
                >
                  ×
                </button>
              </div>
              {(felUpTo || felPris) && <div className="faltfel">{felUpTo ?? felPris}</div>}
            </div>
          );
        })}
        <button
          type="button"
          className="btn btn-g lagg-till"
          onClick={() => andra({ trappa: { ...t, steg: [...t.steg, { upTo: '', pris: '' }] } })}
        >
          + Lägg till steg
        </button>
        {hogsta && (
          <div className="hjalp">
            Över {hogsta} {drivare(t.drivare).kort.replace('/mån', ' per månad')} går förfrågan till
            granskning.
          </div>
        )}
      </fieldset>
    </>
  );
}

function Timpris({
  f,
  fel,
  andra,
}: {
  f: TjanstForm;
  fel: Fel;
  andra: (nytt: Partial<TjanstForm>) => void;
}) {
  const t = f.timpris;
  const satt = (nytt: Partial<TjanstForm['timpris']>) => andra({ timpris: { ...t, ...nytt } });
  const norm = (i: number, ny: Tidsnorm) =>
    satt({ normer: t.normer.map((n, j) => (j === i ? ny : n)) });
  const perPeriod = { monthly: 'per månad', yearly: 'per år', once: 'totalt' }[f.billing];
  return (
    <>
      <Falt etikett="Timpris" fel={fel['timpris.pris']} efter="kr per timme">
        {(p) => (
          <input
            className="in mono tal"
            inputMode="decimal"
            {...p}
            value={t.pris}
            onChange={(e) => satt({ pris: e.target.value })}
          />
        )}
      </Falt>
      <fieldset className="grupp-falt">
        <legend className="etikett">Så lång tid tar det</legend>
        <div className="hjalp">AI uppskattar bara volymen. Tiden räknas med era egna normer.</div>
        {fel['timpris.normer'] && <div className="faltfel">{fel['timpris.normer']}</div>}
        {t.normer.map((n, i) => (
          <div key={i}>
            <div className="tidsnorm">
              <input
                className="in mono tal"
                inputMode="decimal"
                aria-label={`Minuter, tidsnorm ${i + 1}`}
                aria-invalid={fel[`timpris.normer.${i}`] ? true : undefined}
                value={n.minuter}
                onChange={(e) => norm(i, { ...n, minuter: e.target.value })}
              />
              <select
                className="in"
                aria-label={`Vad tiden gäller, tidsnorm ${i + 1}`}
                value={n.slag === 'drivare' ? n.drivare : ''}
                onChange={(e) =>
                  norm(
                    i,
                    e.target.value === ''
                      ? { slag: 'fast', minuter: n.minuter }
                      : { slag: 'drivare', drivare: e.target.value, minuter: n.minuter },
                  )
                }
              >
                {DRIVARE.map((d) => (
                  <option key={d.kod} value={d.kod}>
                    min per {d.enhet}
                  </option>
                ))}
                {n.slag === 'drivare' && !DRIVARE.some((d) => d.kod === n.drivare) && (
                  <option value={n.drivare}>min per {n.drivare}</option>
                )}
                <option value="">min fast {perPeriod}</option>
              </select>
              <button
                type="button"
                className="btn btn-g ta-bort"
                aria-label={`Ta bort tidsnorm ${i + 1}`}
                onClick={() => satt({ normer: t.normer.filter((_, j) => j !== i) })}
              >
                ×
              </button>
            </div>
            {fel[`timpris.normer.${i}`] && (
              <div className="faltfel">{fel[`timpris.normer.${i}`]}</div>
            )}
          </div>
        ))}
        <button
          type="button"
          className="btn btn-g lagg-till"
          onClick={() =>
            satt({
              normer: [
                ...t.normer,
                { slag: 'drivare', drivare: 'transactionsPerMonth', minuter: '' },
              ],
            })
          }
        >
          + Lägg till tidsnorm
        </button>
      </fieldset>
      <div className="tva">
        <Falt etikett="Minst" fel={fel['timpris.minTimmar']} efter={`tim ${perPeriod}`}>
          {(p) => (
            <input
              className="in mono tal"
              inputMode="decimal"
              {...p}
              value={t.minTimmar}
              onChange={(e) => satt({ minTimmar: e.target.value })}
            />
          )}
        </Falt>
        <Falt etikett="Avrunda till">
          {(p) => (
            <select
              className="in"
              {...p}
              value={t.avrunda}
              onChange={(e) => satt({ avrunda: e.target.value })}
            >
              {AVRUNDNINGAR.map((a) => (
                <option key={a.varde} value={a.varde}>
                  {a.namn}
                </option>
              ))}
            </select>
          )}
        </Falt>
      </div>
      <fieldset className="grupp-falt">
        <legend className="etikett">Så visas det i offerten</legend>
        <div className="val-lista">
          {VISNINGAR.map((v) => (
            <label key={v.kod} className={`val${t.visning === v.kod ? ' valt' : ''}`}>
              <input
                type="radio"
                name="visning"
                checked={t.visning === v.kod}
                onChange={() => satt({ visning: v.kod })}
              />
              <span>
                <span className="val-namn">{v.namn}</span>
                <span className="val-text">{v.beskrivning}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>
    </>
  );
}

/**
 * Bolagsformer där tjänsten alltid tas med i offerten, till exempel bokslutet
 * (ADR 0101). Går bara när priset inte beror på en mängd.
 */
function AlltidMed({ f, andra }: { f: TjanstForm; andra: (a: Partial<TjanstForm>) => void }) {
  const mojliga = mojligaAlltidMed(f);
  if (mojliga.length === 0) return null;
  return (
    <fieldset className="grupp-falt">
      <legend className="etikett">Alltid med i offerten för</legend>
      <div className="hjalp" id="alltid-med-hjalp">
        Tas med även när AI inte väljer tjänsten, till exempel bokslutet för aktiebolag.
      </div>
      <div className="kryssrad">
        {BOLAGSFORMER.filter((b) => mojliga.includes(b.kod)).map((b) => (
          <label key={b.kod} className="kryss">
            <input
              type="checkbox"
              aria-describedby="alltid-med-hjalp"
              checked={f.alltidMed.includes(b.kod)}
              onChange={(e) =>
                andra({
                  alltidMed: e.target.checked
                    ? [...f.alltidMed, b.kod]
                    : f.alltidMed.filter((k) => k !== b.kod),
                })
              }
            />
            {b.namn}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
